import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
} from '@mui/material';
import DepartmentSelect from '../Departments/DepartmentSelect';

const fontBase = { fontFamily: 'Roboto, Arial, sans-serif' };

/**
 * Confirm a decision on a supplier registration, with the reason behind it.
 *
 * Approve and reject used to fire straight off a table row, and a rejection was
 * recorded with no reason at all — the supplier got a notification saying only
 * that they had not been approved, and nothing in the audit log said why. The
 * comment box exists on both surfaces (the queue and the review page) so a
 * rejection cannot be recorded blind from either.
 *
 * A reason is required to reject and optional to approve.
 */
const RegistrationDecisionDialog = ({
  open,
  status,
  supplierName,
  submitting = false,
  onCancel,
  onConfirm,
}) => {
  const [comment, setComment] = useState('');
  const [department, setDepartment] = useState('');
  const [touched, setTouched] = useState(false);

  const isRejection = status === 'rejected';

  // A comment left over from the previous registration would be attributed to
  // this one, so clear it whenever the dialog opens.
  useEffect(() => {
    if (open) {
      setComment('');
      setDepartment('');
      setTouched(false);
    }
  }, [open, status]);

  const missingReason = isRejection && !comment.trim();

  const handleConfirm = () => {
    setTouched(true);
    if (missingReason) return;
    onConfirm({ status, comment: comment.trim(), department });
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ ...fontBase, fontWeight: 600, fontSize: '18px', color: '#111827' }}>
        {isRejection ? 'Reject registration' : 'Approve registration'}
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ ...fontBase, color: '#4b5563', fontSize: '14px', mb: 2 }}>
          {isRejection ? (
            <>
              {supplierName || 'This supplier'} will be told their registration was not
              approved, and the reason below is sent to them.
            </>
          ) : (
            <>
              {supplierName || 'This supplier'} will be onboarded, their application
              started, and they will be asked to upload their KYC documents.
            </>
          )}
        </Typography>

        {!isRejection && (
          <Box sx={{ mb: 2.5 }}>
            <DepartmentSelect
              value={department}
              onChange={setDepartment}
              label="Owning department"
              helperText="Optional now — it must be set before their application can be approved."
            />
          </Box>
        )}

        <TextField
          fullWidth
          multiline
          minRows={3}
          autoFocus
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onBlur={() => setTouched(true)}
          label={isRejection ? 'Reason for rejection' : 'Comment (optional)'}
          placeholder={
            isRejection
              ? 'Explain what was wrong so they can correct it'
              : 'Anything the supplier should know'
          }
          error={touched && missingReason}
          helperText={touched && missingReason ? 'A reason is required to reject a registration' : ' '}
          sx={{ ...fontBase, '& .MuiInputBase-input': { fontSize: '14px' } }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={onCancel}
            disabled={submitting}
            sx={{ ...fontBase, textTransform: 'none', color: '#374151' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={submitting || missingReason}
            sx={{
              ...fontBase,
              textTransform: 'none',
              bgcolor: isRejection ? '#dc2626' : '#578A18',
              '&:hover': { bgcolor: isRejection ? '#b91c1c' : '#467014' },
            }}
          >
            {submitting ? 'Saving…' : isRejection ? 'Reject' : 'Approve'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default RegistrationDecisionDialog;
